import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Calendar, Clock, MapPin, FileText, Users, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppointmentModal = ({ isOpen, onClose, lead, getAuthHeader, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    scheduled_time: '',
    location: '',
    lead_name: '',
    lead_email: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);

  // Fetch leads and properties on mount
  useEffect(() => {
    if (isOpen) {
      fetchLeads();
      fetchProperties();
      
      // If a lead is passed in, pre-populate the form
      if (lead) {
        setSelectedLead(lead);
        setFormData(prev => ({
          ...prev,
          lead_name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
          lead_email: lead.email || '',
          notes: generateLeadNotes(lead)
        }));
      }
    }
  }, [isOpen, lead]);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`, {
        headers: getAuthHeader()
      });
      setLeads(response.data || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const fetchProperties = async () => {
    try {
      // Assuming you have a properties endpoint
      // If not, we can create mock data or skip this
      const response = await axios.get(`${API}/properties`, {
        headers: getAuthHeader()
      });
      setProperties(response.data || []);
    } catch (error) {
      // If properties endpoint doesn't exist, use empty array
      console.log('Properties endpoint not available');
      setProperties([]);
    }
  };

  const generateLeadNotes = (leadData) => {
    if (!leadData) return '';
    
    const notes = [];
    notes.push(`Lead: ${leadData.first_name || ''} ${leadData.last_name || ''}`.trim());
    if (leadData.email) notes.push(`Email: ${leadData.email}`);
    if (leadData.phone) notes.push(`Phone: ${leadData.phone}`);
    if (leadData.status) notes.push(`Status: ${leadData.status}`);
    if (leadData.source) notes.push(`Source: ${leadData.source}`);
    if (leadData.ai_score) notes.push(`AI Score: ${leadData.ai_score}/100`);
    if (leadData.buying_timeline) notes.push(`Timeline: ${leadData.buying_timeline}`);
    if (leadData.budget) notes.push(`Budget: ${leadData.budget}`);
    if (leadData.property_type) notes.push(`Property Type: ${leadData.property_type}`);
    if (leadData.notes) notes.push(`\nAdditional Notes:\n${leadData.notes}`);
    
    return notes.join('\n');
  };

  const handleLeadSelect = (e) => {
    const leadId = e.target.value;
    if (!leadId) {
      setSelectedLead(null);
      setFormData(prev => ({ ...prev, lead_name: '', lead_email: '', notes: '' }));
      return;
    }
    
    const selected = leads.find(l => l.id === leadId);
    if (selected) {
      setSelectedLead(selected);
      setFormData(prev => ({
        ...prev,
        lead_name: `${selected.first_name || ''} ${selected.last_name || ''}`.trim(),
        lead_email: selected.email || '',
        notes: generateLeadNotes(selected)
      }));
    }
  };

  const handlePropertySelect = (e) => {
    const propertyAddress = e.target.value;
    if (propertyAddress) {
      setFormData(prev => ({ ...prev, location: propertyAddress }));
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate title is not empty
      if (!formData.title || !formData.title.trim()) {
        toast.error('Appointment title is required');
        setLoading(false);
        return;
      }

      const appointmentData = {
        title: formData.title.trim(),
        lead_id: selectedLead?.id || null,
        scheduled_time: formData.scheduled_time,
        lead_name: formData.lead_name || (selectedLead ? `${selectedLead.first_name || ''} ${selectedLead.last_name || ''}`.trim() : null),
        lead_phone: selectedLead?.phone || null,
        lead_email: formData.lead_email || selectedLead?.email || null,
        location: formData.location,
        notes: formData.notes
      };

      await axios.post(`${API}/appointments`, appointmentData, {
        headers: getAuthHeader()
      });

      toast.success('Appointment booked successfully!');
      onSuccess?.();
      onClose();
      
      // Reset form
      setFormData({
        title: '',
        scheduled_time: '',
        location: '',
        lead_name: '',
        lead_email: '',
        notes: ''
      });
      setSelectedLead(null);
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-700">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 dark:bg-slate-900 border-b border-slate-700 p-6 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-white">Book Appointment</h2>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white transition-colors p-2 hover:bg-slate-700 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Select Existing Lead - ALWAYS SHOW */}
          {leads.length > 0 && (
            <div>
              <Label className="block text-sm font-medium text-white mb-2">
                <Users className="w-4 h-4 inline mr-2" />
                Select Lead (Optional)
              </Label>
              <select
                onChange={handleLeadSelect}
                value={selectedLead?.id || ''}
                className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- New appointment (no lead) --</option>
                {leads.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.first_name} {l.last_name} ({l.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Title */}
          <div>
            <Label className="block text-sm font-medium text-white mb-2">
              <FileText className="w-4 h-4 inline mr-2" />
              Appointment Title *
            </Label>
            <Input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Property Viewing, Consultation Meeting"
              className="bg-slate-700 border-slate-600 text-white placeholder:text-gray-400"
              required
            />
          </div>

          {/* Date & Time */}
          <div>
            <Label className="block text-sm font-medium text-white mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Date & Time *
            </Label>
            <Input
              type="datetime-local"
              name="scheduled_time"
              value={formData.scheduled_time}
              onChange={handleChange}
              className="bg-slate-700 border-slate-600 text-white"
              required
            />
          </div>

          {/* Lead Name (if not selected from dropdown) */}
          {!selectedLead && (
            <div>
              <Label className="block text-sm font-medium text-white mb-2">
                <Users className="w-4 h-4 inline mr-2" />
                Person Name
              </Label>
              <Input
                type="text"
                name="lead_name"
                value={formData.lead_name}
                onChange={handleChange}
                placeholder="e.g., John Smith"
                className="bg-slate-700 border-slate-600 text-white placeholder:text-gray-400"
              />
            </div>
          )}

          {/* Lead Email (if not selected from dropdown) */}
          {!selectedLead && (
            <div>
              <Label className="block text-sm font-medium text-white mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Email Address
              </Label>
              <Input
                type="email"
                name="lead_email"
                value={formData.lead_email}
                onChange={handleChange}
                placeholder="e.g., john@example.com"
                className="bg-slate-700 border-slate-600 text-white placeholder:text-gray-400"
              />
            </div>
          )}

          {/* Property Selection Dropdown */}
          {properties.length > 0 && (
            <div>
              <Label className="block text-sm font-medium text-white mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                Select Property (Optional)
              </Label>
              <select
                onChange={handlePropertySelect}
                className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select a property --</option>
                {properties.map((property, idx) => (
                  <option key={idx} value={property.address}>
                    {property.address} - {property.type || 'Property'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Location with Google Maps Link */}
          <div>
            <Label className="block text-sm font-medium text-white mb-2">
              <MapPin className="w-4 h-4 inline mr-2" />
              Location
            </Label>
            <div className="flex gap-2">
              <Input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., 123 Main St, Saint John, NB"
                className="flex-1 bg-slate-700 border-slate-600 text-white placeholder:text-gray-400"
              />
              {formData.location && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.location)}`;
                    window.open(mapsUrl, '_blank');
                  }}
                  className="px-4 border-slate-600 text-white hover:bg-slate-700"
                  title="Open in Google Maps"
                >
                  <MapPin className="w-4 h-4" />
                </Button>
              )}
            </div>
            {formData.location && (
              <p className="mt-2 text-xs text-gray-300">
                Click the map icon to view location in Google Maps
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label className="block text-sm font-medium text-white mb-2">
              <FileText className="w-4 h-4 inline mr-2" />
              Notes
            </Label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any additional details about the appointment..."
              rows="4"
              className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 text-white placeholder:text-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 border-slate-600 text-white hover:bg-slate-700"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              disabled={loading}
            >
              {loading ? 'Booking...' : 'Book Appointment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentModal;
